/* AUTO-SYNCED from PsyKit.io/lib/compliance/minorConsent.ts — DO NOT EDIT. Regenerate with `npm run sync`. */
/**
 * lib/compliance/minorConsent.ts
 *
 * Pure, side-effect-free legal core for minor-consent classification.
 * Single source of truth shared by the client-creation routes.
 *
 * Two distinct ages:
 *   - AGE_OF_MAJORITY (18): is minor-specific handling needed at all?
 *   - effective_minor_consent_age (state engine, e.g. WA 13): can a minor self-consent?
 */
import { MINOR_ONLY_FORM_TYPES } from "@/lib/consent-forms";

export const AGE_OF_MAJORITY = 18;

export type SignerRole = "self" | "minor_self" | "guardian" | "minor_assent";
export type ConsentPath = "parental" | "minor_self" | "dual" | null;

/** Decimal age in years. Centralized so all callers agree. */
export function ageInYears(dob: Date, now: Date): number {
  return (now.getTime() - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
}

/** The legal heart: age + state self-consent age → consent path. */
export function classifyMinorConsent(params: {
  ageYears: number;
  effectiveMinorConsentAge: number;
}): { isMinor: boolean; consentPath: ConsentPath; minorRecordAccessRestricted: boolean } {
  const isMinor = params.ageYears < AGE_OF_MAJORITY;
  if (!isMinor) return { isMinor, consentPath: null, minorRecordAccessRestricted: false };
  if (params.ageYears >= params.effectiveMinorConsentAge)
    return { isMinor, consentPath: "minor_self", minorRecordAccessRestricted: true };
  return { isMinor, consentPath: "parental", minorRecordAccessRestricted: false };
}

/** Which token(s) to mint for a path. Two for parental (guardian + assent). */
export function tokenRolesForPath(consentPath: ConsentPath): SignerRole[] {
  // Minor assent was dropped — it isn't legally required for parent-authorized
  // care, so the parental path now needs only the guardian's signature.
  // "minor_assent" stays a dormant SignerRole, revivable if a clinician ever
  // wants optional assent (mirrors how the legacy 'dual' value stays dormant).
  if (consentPath === "parental") return ["guardian"];
  if (consentPath === "minor_self" || consentPath === "dual") return ["minor_self"];
  return ["self"];
}

/** The operative recipient email for a token role. Guardian → guardian's email;
 *  everyone else (incl. minor_assent) → the client's own email (may be relayed). */
export function pickRecipientEmail(params: {
  role: SignerRole; clientEmail: string; guardianEmail?: string | null;
}): string | null {
  if (params.role === "guardian") return params.guardianEmail?.trim() || null;
  return params.clientEmail?.trim() || null;
}

export interface ConsentDocSpec {
  form_type: string;
  required_by_states: string[];
  required_signer_role: SignerRole;
}

/**
 * Build the consent document set + each doc's required signer role from the path.
 * `stateForms` are the state-engine forms (already adult-filtered upstream). Any
 * minor-only form there is dropped and replaced by the path-specific minor form(s),
 * so guardian-vs-self is chosen by PATH, not by what a state happened to list.
 */
export function buildConsentDocSet(params: {
  consentPath: ConsentPath;
  stateForms: { form_type: string; required_by_states: string[] }[];
}): ConsentDocSpec[] {
  const { consentPath, stateForms } = params;
  const baseRole: SignerRole =
    consentPath === "parental" ? "guardian" :
    consentPath === "minor_self" || consentPath === "dual" ? "minor_self" :
    "self";

  const docs: ConsentDocSpec[] = [
    { form_type: "npp", required_by_states: ["HIPAA"], required_signer_role: baseRole },
  ];
  for (const f of stateForms) {
    if (f.form_type === "npp") continue;
    if (MINOR_ONLY_FORM_TYPES.has(f.form_type)) continue; // replaced by path-specific below
    docs.push({ ...f, required_signer_role: baseRole });
  }
  if (consentPath === "parental") {
    // Guardian signs the legal consent. No separate minor-assent doc — assent
    // isn't legally required and is best handled as an in-session conversation.
    docs.push({ form_type: "minor_guardian_consent", required_by_states: ["minor"], required_signer_role: "guardian" });
  } else if (consentPath === "minor_self" || consentPath === "dual") {
    docs.push({ form_type: "minor_self_consent", required_by_states: ["minor"], required_signer_role: "minor_self" });
  }
  return docs;
}
